/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { CalculatorFormData } from "../../models/http/calculator/calculator-form-data.model";
import { CalculatorOptions } from "../../models/http/calculator/calculator-options.model";
import { CalculatorResponse } from "../../models/http/calculator/calculator-response.model";
import { CloudProviderOptions } from "../../models/http/calculator/cloud-provider-options.model";
import { OnPremiseOptions } from "../../models/http/calculator/on-premise-options.model";
import { CommunicationService } from "../communication.service";
import { HttpMethods } from "../../models/ui/http-methods.model";
import { ENDPOINT_GROUPS } from "../../utils/apis/apis.constants";

@Injectable({
	providedIn: "root"
})
export class CalculatorHttpService {
	private _calculatorOperations = ENDPOINT_GROUPS.calculator.operations;

	constructor(private _communicationService: CommunicationService) {}

	/**
	 * Retrieves initial calculator form data from the server.
	 *
	 * @returns An observable containing the calculator form data.
	 */
	public getCalculatorFormData(): Observable<CalculatorFormData> {
		return this._communicationService.callService(
			this._calculatorOperations.getCalculatorFormData.service,
			HttpMethods.GET
		);
	}

	/**
	 * Retrieves area, region, cpu and gpu options for on-premise environments.
	 *
	 * @returns An observable containing the on-premise area and region options.
	 */
	public getOnPremiseOptions(): Observable<OnPremiseOptions> {
		return this._communicationService.callService(
			this._calculatorOperations.getOnPremiseOptions.service,
			HttpMethods.GET
		);
	}

	/**
	 * Retrieves area, region and cpu options for a specified cloud provider.
	 *
	 * @param id - The unique identifier of the cloud provider.
	 * @returns An Observable that emits the cloud provider options.
	 */
	public getCloudProviderOptions(id: string): Observable<CloudProviderOptions> {
		const pathParams = { id };

		return this._communicationService.callService(
			this._calculatorOperations.getCloudProviderOptions.service,
			HttpMethods.GET,
			{ pathParams }
		);
	}

	/**
	 * Initiates the calculator with the specified options for a given tenant.
	 *
	 * @param tenantId - The unique identifier of the tenant.
	 * @param calculatorOptions - The options to configure the calculator.
	 * @returns An Observable that emits the response from the calculator service.
	 */
	public startCalculator(
		tenantId: string,
		calculatorOptions: CalculatorOptions
	): Observable<CalculatorResponse> {
		const pathParams = { tenantId };

		return this._communicationService.callService(
			this._calculatorOperations.startCalculator.service,
			HttpMethods.POST,
			{ pathParams },
			calculatorOptions
		);
	}
}
